import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useMatch } from "react-router";
import _ from "lodash";

import { BASE_URL, URLS } from "config/urls";
import { colors } from "theme/palette";
import { SUPPORT_EMAIL } from "utils/constants";
import { getItem, getToken, IDLE_TIME, saveToken } from "utils/storage";

import {
  MAX_IDLE_TIME,
  TOKEN_REFRESH_INTERVAL,
} from "components/AutomaticLogout/constants";
import FreshworksWidget from "components/FreshworksWidget";
import GlobalNotifications from "components/GlobalNotifications";
import Link from "components/Link";
import Stack from "components/Stack";
import Text from "components/Text";
import { FeatureDisabledContextProvider } from "components/useFeatureDisabledDialog";
import { FeatureLimitContextProvider } from "components/useFeatureLimitDialog";

import { getTokenBySession, useSignedUser } from "entities/Auth/sdk";

const PrivateRoutesGuard = () => {
  const location = useLocation();
  const [userAccountIsDisabled, setUserAccountIsDisabled] = useState(false);
  const [token, setToken] = useState(getToken());
  const [, setLastKnownUserId] = useState<number | null>(null);
  const { data: user, mutate: refetchUserData } = useSignedUser();

  const redirectToLogin = useCallback(() => {
    const searchParams = _.isEmpty(location.search)
      ? `?next=${window.location.href}`
      : location.search;

    // If the current session on the backend didn't return a token - reroute to login
    window.location.href = `${BASE_URL}v2/users/login/${searchParams}`;
  }, [location]);

  useEffect(() => {
    if (!token) {
      // This is a part of our login flow
      // If there's a session, created by auth0, in the back-end - we will get our auth token here.
      getTokenBySession()
        .then(({ token }) => {
          // Save the returned token and re-render the current component
          saveToken(token);
          setToken(token);
        })
        .catch((error) => {
          if (error.message === "User account is disabled.") {
            setUserAccountIsDisabled(true);
            return;
          }

          redirectToLogin();
        });
    }
  }, [token, redirectToLogin]);

  useEffect(() => {
    if (!_.isNil(token)) {
      const newTokenRefreshInterval = setInterval(() => {
        const idleTime = parseInt(getItem(IDLE_TIME) || "0");

        // Idle time usually is set to the last moment the user was active with MAX_IDLE_TIME added to it.
        // If the user has been active in the last 5 minutes, we want to refresh the token.
        if (
          idleTime > 0 &&
          idleTime - MAX_IDLE_TIME * 60000 > Date.now() - TOKEN_REFRESH_INTERVAL
        ) {
          getTokenBySession().then(({ token }) => {
            // Save the returned token and re-render the current component
            saveToken(token);
            setToken(token);
          });
        }
      }, TOKEN_REFRESH_INTERVAL);

      return () => clearInterval(newTokenRefreshInterval);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      refetchUserData();
    }
  }, [token, refetchUserData]);

  const storageEventListener = useCallback(() => {
    refetchUserData();
  }, [refetchUserData]);

  useEffect(() => {
    window.addEventListener("storage update", storageEventListener);

    return () => {
      window.removeEventListener("storage update", storageEventListener);
    };
  }, [storageEventListener]);

  useEffect(() => {
    if (!_.isNil(user)) {
      setLastKnownUserId((prevValue) => {
        if (!_.isNil(prevValue) && prevValue !== user.id) {
          window.location.reload();
        }

        return user.id;
      });
    }
  }, [user]);

  const match = useMatch(URLS.PRINTABLE_CHECKLIST);

  const shouldInludeFreshworksWidget = useMemo(() => _.isNil(match), [match]);

  if (userAccountIsDisabled) {
    return (
      <Stack
        sx={{
          top: 0,
          left: 0,
          padding: 5,
          zIndex: 1001,
          width: "100vw",
          height: "100vh",
          position: "absolute",
          backgroundColor: colors.white,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="inactive-account-page"
      >
        <Text
          variant="h1"
          textAlign="center"
          data-testid="inactive-account-message"
        >
          Your account has not been activated yet. Please contact your company
          admin or email us at{" "}
          <Link
            to={`mailto:${SUPPORT_EMAIL}`}
            style={{ color: colors.blue100 }}
            data-testid="contact-support-link"
          >
            {SUPPORT_EMAIL}
          </Link>{" "}
          to do so.
        </Text>
      </Stack>
    );
  }

  if (!token) {
    return null;
  }

  return (
    <FeatureLimitContextProvider>
      <FeatureDisabledContextProvider>
        <GlobalNotifications />
        {shouldInludeFreshworksWidget && <FreshworksWidget />}
        <Outlet />
      </FeatureDisabledContextProvider>
    </FeatureLimitContextProvider>
  );
};

export default PrivateRoutesGuard;
